import type { AnyAction, Middleware } from 'redux';
import { createAudioInfoConnection } from '../../../services/WsConnection';
import {
    audioSystemInfoConnectionClosed,
    audioSystemInfoConnectionOpened,
    stopAudioSystemInfoConnection,
    types as mainTypes
} from '../../actions/backendMain';
import { types as commonTypes, reload } from '../../actions/backendCommon';
import { captureException } from '../../../tracker/raven';
import { isAudioSystemInfoConnectionOpen } from '../../selectors/call';
import type { RootState } from '../../types';
import { errorCodes } from '../../../utils/utils';
import { SysInfoConn } from '../../../errors/Errors';
import { audioSysLogger } from '../../../utils/logger/loggerFactory';
import { dispatchBackend } from '../../types/backend';

const { AUDIO_DEVICES_CONFIG_CHANGED, AUDIO_SYSTEM_INFO_CONNECTION_CLOSED } = errorCodes.background;

const audioSystemInfoConnection: Middleware = store => {
    const getState = store.getState as () => RootState;

    function createAudioSystemInfoConnection(port: number) {
        const ws = createAudioInfoConnection({ host: 'localhost', port });

        ws.on('open', () => dispatchBackend(store.dispatch, audioSystemInfoConnectionOpened()));
        ws.on('close', error => dispatchBackend(store.dispatch, audioSystemInfoConnectionClosed(error.wasClean)));
        ws.on('message', () => {
            dispatchBackend(
                store.dispatch,
                reload({
                    code: AUDIO_DEVICES_CONFIG_CHANGED,
                    reason: 'AUDIO_DEVICES_CONFIG_CHANGED'
                })
            );
        });

        return ws;
    }
    let connection: { close: () => void } | null = null;

    return next => action => {
        const typedAction = action as AnyAction;
        const { type, payload } = typedAction;
        const result = next(action);
        const state = getState();

        switch (type) {
            case commonTypes.RELOAD: {
                if (isAudioSystemInfoConnectionOpen(state)) {
                    dispatchBackend(store.dispatch, stopAudioSystemInfoConnection());
                }
                break;
            }
            case mainTypes.AUDIO_SYSTEM_INFO_CONNECTION_CLOSED: {
                dispatchBackend(
                    store.dispatch,
                    reload({
                        code: AUDIO_SYSTEM_INFO_CONNECTION_CLOSED,
                        reason: 'AUDIO_SYSTEM_INFO_CONNECTION_CLOSED'
                    })
                );
                break;
            }
            case mainTypes.STOP_AUDIO_SYSTEM_INFO_CONNECTION: {
                if (connection && isAudioSystemInfoConnectionOpen(state)) {
                    connection.close();
                    connection = null;
                }
                break;
            }
            case mainTypes.START_AUDIO_SYSTEM_INFO_CONNECTION: {
                if (!connection) {
                    connection = createAudioSystemInfoConnection(payload.port);
                } else {
                    const error = new SysInfoConn.AlreadyStartedError();
                    captureException(error);
                    audioSysLogger.error(error.message, error);
                }
                break;
            }
            default:
        }

        return result;
    };
};

export default audioSystemInfoConnection;
